import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isResponsibleForInstantConnectivity,
  supportsInstantConnectivity,
} from 'support/helpers/relationships/relationships';
import { RelationshipV2 } from 'support/types';
import { ConnectionSetupTableRow } from 'components/ConnectionSetupTable/ConnectionSetupTable';
import { StepStatus } from 'components/Display/Steps/Steps';

type Params = {
  stepStatus: StepStatus;
  relationship: RelationshipV2 | undefined;
  isSender: boolean;
  routeTo: (subPath: 'instantConnectivity', params: Record<string, string>) => string;
  partnerId: string;
};

export const useConfigurationStep = ({ stepStatus, relationship, isSender, routeTo, partnerId }: Params) => {
  const { t } = useTranslation();

  const showInstantConnectivity = supportsInstantConnectivity(relationship);

  const isResponsible = isResponsibleForInstantConnectivity(relationship?.message_type, isSender);

  const rows: Array<ConnectionSetupTableRow> = useMemo(
    () => [
      ...(showInstantConnectivity && relationship
        ? [
            {
              id: 'instant-connectivity',
              status: stepStatus === 'completed' ? ('DONE' as const) : ('TODO' as const),
              title: t('connections:relationshipDetails.steps.configuration.tasks.instantConnectivity', {
                documentType: t(`common:messageTypes.singular.${relationship.message_type}`),
                documentTypePlural: t(`common:messageTypes.plural.${relationship.message_type}`),
              }),
              ownership: isResponsible ? ('you' as const) : ('trade_partner' as const),
              actionRequired: isResponsible && stepStatus === 'current',
              path: isResponsible
                ? routeTo('instantConnectivity', { partnerId, relationshipId: relationship.id })
                : undefined,
            },
          ]
        : []),
      {
        id: 'technical-configuration',
        status: stepStatus === 'completed' ? 'DONE' : 'TODO',
        title: t('connections:relationshipDetails.steps.configuration.tasks.technicalConfiguration'),
        ownership: 'procuros',
        actionRequired: false,
      },
    ],
    [showInstantConnectivity, relationship, stepStatus, isResponsible, routeTo, partnerId, t],
  );

  return { rows };
};
